import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Skeleton, CircularProgress, Typography, Tooltip } from '@mui/material';
import {
  FlowResourceType,
  flowsQuery,
  generateTestIds,
  useDialogs,
  useNotifications,
  useEditorStore,
  getResponseErrorMessage,
  useFlowBuilder2ResetFlowMutation,
  ResetIcon,
} from '@diana/core';
import { useFlowBuilder2PageContext } from '../../Context/useFlowBuilder2PageContext';
import { StyledIconButton } from '../../FlowBuilder2Page.styles';
import { FLOW_BUILDER2_PAGE_NS } from '../../i18n';
import { useFlowBuilder2PageStore } from '../../Store/useFlowBuilder2PageStore';
import { useReloadBuilder } from '../../Support/useReloadBuilder';

const ResetChangesIconButtonTestIds = generateTestIds('FlowBuilderPage', ['resetChangesButton']);

export const ResetChangesIconButton = () => {
  const { isLoading: isLoadingPage, error: pageError } = useFlowBuilder2PageContext();

  const { t } = useTranslation(FLOW_BUILDER2_PAGE_NS);
  const { confirm } = useDialogs();
  const notifications = useNotifications();
  const reloadBuilder = useReloadBuilder();

  const flowId = useEditorStore((s) => s.flow?.id ?? '');
  const flowResourceType = useEditorStore((s) => s.flow?.properties.flowResourceType);
  const resetEditPending = useFlowBuilder2PageStore((s) => s.resetEditPending);

  const getPublishedFlowQuery = flowsQuery.getById({ id: flowId });
  const { data: publishedFlow } = useQuery({
    queryKey: getPublishedFlowQuery.key,
    queryFn: getPublishedFlowQuery.fn,
    enabled: !!flowId,
  });

  const { mutate: resetFlow, isPending: isResettingFlow } = useFlowBuilder2ResetFlowMutation({
    onSuccess: () => {
      const flowTypeName =
        flowResourceType === FlowResourceType.Entity ? t('labels.assetType') : t('labels.workflow');
      reloadBuilder();
      resetEditPending();

      notifications.success({
        message: t('descriptions.flowTypeNameResetSuccess', { flowTypeName }),
        description: t('titles.flowTypeNameReset', { flowTypeName }),
      });
    },
    onError: (error) => {
      notifications.error({
        message: t('descriptions.tryRefreshOrContactSupport'),
        description: getResponseErrorMessage(error),
      });
    },
  });

  const disabled = useEditorStore((s) => {
    if (!publishedFlow || !s.flow) return true;
    if (publishedFlow.id === s.flow.id) return true;
    return publishedFlow.draftFlowId !== s.flow.id;
  });

  const onResetChanges = async () => {
    const title = (
      <Typography>
        {t('prompts.areYouSUreToResetFlowTypeName', {
          flowTypeName:
            flowResourceType === FlowResourceType.Entity
              ? t('labels.assetType').toLowerCase()
              : t('labels.workflow').toLowerCase(),
        })}
      </Typography>
    );
    const message = t('descriptions.unpublishedChangesWillNotBeSaved');
    const { confirmed } = await confirm(message, {
      size: 'sm',
      title,
      variant: 'warning',
      confirmButtonLabel: t('buttons.resetChanges'),
    });
    if (confirmed) resetFlow({ flowId });
  };

  if (pageError) return <Fragment />;

  if (isLoadingPage) return <Skeleton variant="circular" width={32} height={32} />;

  return (
    <Tooltip title={t('tooltips.resetToLastPublishedVersion')}>
      <StyledIconButton
        disabled={disabled || isResettingFlow}
        onClick={onResetChanges}
        data-testid={ResetChangesIconButtonTestIds.resetChangesButton}
      >
        {isResettingFlow ? (
          <CircularProgress size={20} />
        ) : (
          <ResetIcon color={disabled ? 'inherit' : undefined} fontSize="small" />
        )}
      </StyledIconButton>
    </Tooltip>
  );
};
